import { AccountTypes } from '@interfaces/models/account-types';
import { IndividualDesignationListedTypes } from '@interfaces/models/individual-designation-types';
import { IReceipt, ReceiptRow } from '@interfaces/models/receipt.interface';
import {
  SettlementFundAoAEnum,
  SettlementFundMap,
} from '@interfaces/settlement-fund/settlement-fund.enum';
import {
  FlowTypes,
  InvestmentObjectives,
  IRAOptions,
  IRATypes,
  ISelectedAccount,
  ISelectedClientAccountsState,
  TransactionTypesEnum,
  urlMapper,
  YesNoEmpty,
} from '@shared/models';
import { ReceiptModal } from '@shared/models/receipt-modal';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import { TransactionState } from '@store/reducers';
import { EstateState } from '@store/reducers/estate/estate.reducer';
import { MfePersonalInfoClientState } from '@store/reducers/mfe-personal-info-client/mfe-personal-info-client.reducer';
import { IOpenAccountState } from '@store/reducers/open-account/open-account.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import { v4 as uuidv4 } from 'uuid';

import { splitClientId } from './new-account/new-account.cashier';
import { buildSettlementFundRow } from './receipt.helpers';

export const IRATransferToType = {
  [IRATypes.OTHER]: 'Inherited IRA Estate Account',
  [IRATypes.ROTH]: 'Inherited Roth IRA Estate Account',
  [IRATypes.INHERITED_OTHER]: 'Inherited IRA Estate Account',
  [IRATypes.INHERITED_ROTH]: 'Inherited Roth IRA Estate Account',
};

export const iraTypeMap = {
  [IRATypes.OTHER]: 'Traditional IRA',
  [IRATypes.ROTH]: 'Roth IRA',
  [IRATypes.INHERITED_OTHER]: 'Inherited IRA',
  [IRATypes.INHERITED_ROTH]: 'Inherited Roth IRA',
  [IRATypes.UNKNOWN]: 'Unknown',
};

function isEstateBrokerage(
  multiEstateToggleOn: boolean,
  accountTypeInheriting: AccountTypes,
  individualDesignation: IndividualDesignationListedTypes,
  isNotSpouse: boolean,
): boolean {
  return (
    multiEstateToggleOn &&
    (accountTypeInheriting === AccountTypes.INDIVIDUAL ||
      accountTypeInheriting === AccountTypes.INDV) &&
    individualDesignation === IndividualDesignationListedTypes.EXECUTOR &&
    isNotSpouse
  );
}

function isTrustBrokerage(
  individualJointToTrustToggleOn: boolean,
  accountTypeInheriting: AccountTypes,
  individualDesignation: IndividualDesignationListedTypes,
  isNotSpouse: boolean,
): boolean {
  return (
    individualJointToTrustToggleOn &&
    (accountTypeInheriting === AccountTypes.INDIVIDUAL ||
      accountTypeInheriting === AccountTypes.INDV ||
      accountTypeInheriting === AccountTypes.JOINT) &&
    individualDesignation === IndividualDesignationListedTypes.TRUSTEE &&
    isNotSpouse
  );
}

export function getMultiInheritanceAccountToType(
  transaction: TransactionState | null,
  multiEstateToggleOn: boolean,
  individualJointToTrustToggleOn: boolean,
): string {
  const accountTypeMap = {
    [AccountTypes.INDIVIDUAL]: 'Individual',
    [AccountTypes.INDV]: 'Individual',
    [AccountTypes.JOINT]: 'Individual',
    [AccountTypes.JOINT_COMMUNITY_PROPERTY]: 'Individual',
    [AccountTypes.JOINT_TENANTS_WROS]: 'Individual',
  };
  if (!transaction) {
    return 'Unknown';
  }

  const { accountTriage, selectedClientAccounts } = transaction;
  const { accountTypeInheriting, isSpouse, isOnlyBeneficiary, individualDesignation } =
    accountTriage;
  const iraType = selectedClientAccounts.transferTo?.iraType;
  const iraOption = selectedClientAccounts.transferTo?.iraOption;

  // inherited ira -> inherited ira
  // inherited roth ira -> inherited roth ira
  if (
    accountTypeInheriting === AccountTypes.IRA &&
    (iraType === IRATypes.INHERITED_OTHER || iraType === IRATypes.INHERITED_ROTH)
  ) {
    return iraTypeMap[iraType];
  }

  // traditional ira spouse assume -> traditional ira
  // roth ira spouse assume -> roth ira
  if (
    accountTypeInheriting === AccountTypes.IRA &&
    isSpouse === 'YES' &&
    (iraType === IRATypes.OTHER || iraType === IRATypes.ROTH) &&
    iraOption === IRAOptions.ASSUME
  ) {
    return iraTypeMap[iraType];
  }

  // traditional ira spouse inheriting/non-spouse/not only bene -> inherited ira
  const isIra = accountTypeInheriting === AccountTypes.IRA && iraType === IRATypes.OTHER;
  const isIraSpouseInherit = isSpouse === 'YES' && iraOption === IRAOptions.INHERIT;
  const isIraNotOnlyBene = isSpouse === 'YES' && isOnlyBeneficiary === YesNoEmpty.NO;
  const isNotSpouse = isSpouse === 'NO' || isSpouse === '';

  if (isIra && (isIraSpouseInherit || isIraNotOnlyBene || isNotSpouse)) {
    return iraTypeMap[IRATypes.INHERITED_OTHER];
  }

  // roth ira spouse inheriting/non-spouse/not only bene -> inherited roth ira
  if (
    accountTypeInheriting === AccountTypes.IRA &&
    iraType === IRATypes.ROTH &&
    (isIraSpouseInherit || isIraNotOnlyBene || isNotSpouse)
  ) {
    return iraTypeMap[IRATypes.INHERITED_ROTH];
  }

  // individual executor -> estate brokerage (multi-estate toggle on)
  if (
    isEstateBrokerage(
      multiEstateToggleOn,
      accountTypeInheriting,
      individualDesignation,
      isNotSpouse,
    )
  ) {
    return 'Estate brokerage';
  }

  // individual/joint trustee -> trust brokerage (individual joint to trust toggle on)
  if (
    isTrustBrokerage(
      individualJointToTrustToggleOn,
      accountTypeInheriting,
      individualDesignation,
      isNotSpouse,
    )
  ) {
    return 'Trust brokerage';
  }

  // joint and individual -> individual
  return accountTypeMap?.[accountTypeInheriting] ?? 'Unknown';
}

export function getMultiInheritanceAccountFromType(transaction: TransactionState | null): string {
  if (!transaction) {
    return 'Unknown';
  }
  const accountType = transaction.accountTriage.accountTypeInheriting;
  const iraType = transaction.selectedClientAccounts.transferTo?.iraType;
  const accountTypeMap = {
    [AccountTypes.INDIVIDUAL]: 'Individual',
    [AccountTypes.INDV]: 'Individual',
    [AccountTypes.JOINT]: 'Joint',
    [AccountTypes.JOINT_COMMUNITY_PROPERTY]: 'Joint',
    [AccountTypes.JOINT_TENANTS_WROS]: 'Joint',
  };

  return (
    accountTypeMap?.[accountType] ??
    (accountType === AccountTypes.IRA ? iraTypeMap[iraType] : 'Unknown')
  );
}

export function joinWithSpace(values: String[]): string {
  return values.filter(Boolean).join(' ');
}

export function formatPhoneNumber(number: string): string {
  if (number.length === 10) {
    return `${number.substring(0, 3)}-${number.substring(3, 6)}-${number.substring(6, 10)}`;
  }
  return number;
}

export function formatSSN(ssn: string): string {
  if (ssn.length === 9) {
    return `${ssn.substring(0, 3)}-${ssn.substring(3, 5)}-${ssn.substring(5, 9)}`;
  }
  return ssn;
}

export function toSentenceLowerCase(string: string) {
  if (!string) return '';
  const str = string.replace(/_/g, ' ');
  return str.charAt(0) + str.substring(1).toLowerCase();
}

export function buildAccountFromNonretirement(
  accountsFrom: ISelectedAccount[],
  accountType: string,
  editable: boolean,
  triageState: TriageState,
  content?: typeof ReviewContent.receiptContent.transferFromAndTo,
): IReceipt {
  const flow = urlMapper[triageState.flowType];
  const receipt: IReceipt = {
    header: content?.transferringFromLabel || 'Transferring from',
    editable: AccountTypes.INDV === accountType ? false : editable,
    editRoute: `secure/${flow}/transfer-from`,
    sectionEvidenceID: 'accountFrom',
    rows: [],
  };
  let fromAccountType = 'fromAccountType';
  let fromAccountRegistration = 'fromAccountRegistration';
  let fromAccountNumber = 'fromAccountNumber';
  if (AccountTypes.INDV === accountType) {
    receipt.rows.push({
      label: content?.accountTypeLabel || 'Account type',
      values: ['Individual account'],
      evidenceID: fromAccountType,
    });
  }
  let counter = 0;
  const accountTitle = AccountTypes.UGMA_UTMA === accountType ? 'UGMA/UTMA' : 'Joint';
  accountsFrom.forEach((account) => {
    fromAccountType = fromAccountType.concat(counter.toString());
    fromAccountRegistration = fromAccountRegistration.concat(counter.toString());
    fromAccountNumber = fromAccountNumber.concat(counter.toString());
    receipt.rows.push(
      {
        label: content?.accountTypeLabel || 'Account type',
        values: [`${accountTitle} account`],
        evidenceID: fromAccountType,
      },
      {
        label: content?.accountRegistrationLabel || 'Account registration',
        values: [accountName(account)],
        evidenceID: fromAccountRegistration,
      },
      {
        label: content?.accountNumberLabel || 'Account number',
        values: [account.accountNumber],
        evidenceID: fromAccountNumber,
      },
    );
    if (account.accountData.nickname) {
      receipt.rows.push({
        label: 'Account nickname',
        values: [account.accountData.nickname],
        evidenceID: 'fromAccountNickname',
      });
    }
    counter++;
  });
  if (AccountTypes.UGMA_UTMA !== accountType) {
    receipt.rows.push({
      label: 'Source of Funds',
      values: ['Family/Inheritance'],
      evidenceID: 'sourceOfFunds',
    });
  }
  return receipt;
}

export function buildToAccountRow(
  personalInfo: MfePersonalInfoClientState['personalInfo'],
  selectedAccounts: ISelectedClientAccountsState,
  openAccount: IOpenAccountState,
  settlementFund: SettlementFundAoAEnum,
  flowType: FlowTypes,
  content?: typeof ReviewContent.receiptContent.transferFromAndTo,
) {
  const toAccountRows: ReceiptRow[] = [];
  const toAccount = selectedAccounts.transferTo.selectedAccount;

  const name = joinWithSpace([
    personalInfo.firstName,
    personalInfo.middleName,
    personalInfo.lastName,
  ]);
  if (
    TransactionTypesEnum.EXISTING === selectedAccounts.transactionType &&
    toAccount?.accountData
  ) {
    const toAccountDetail = toAccount.accountData;
    const accountName = toAccountDetail.accountName;
    // Do not remove this elvis operator. It breaks the selector without it
    const isBrokerage = toAccountDetail.productType?.includes('BROKERAGE');
    toAccountRows.push(
      {
        label: content?.accountTypeLabel || 'Account type',
        values: [toSentenceLowerCase(toAccountDetail.accountType)],
        evidenceID: 'toAccountType',
      },
      {
        label: content?.accountRegistrationLabel || 'Account registration',
        values: [isBrokerage ? accountName.substr(0, accountName.indexOf('—')) : accountName],
        evidenceID: 'toAccountRegistration',
      },
      {
        label: content?.accountRegistrationLabel || 'Account number',
        values: [toAccountDetail.accountNumber],
        evidenceID: 'toAccountNumber',
      },
    );
    if (toAccount.accountData.nickname) {
      toAccountRows.push({
        label: 'Account nickname',
        values: [toAccount.accountData.nickname],
        evidenceID: 'toAccountNickname',
      });
    }
  } else {
    toAccountRows.push(
      {
        label: content?.accountTypeLabel || 'Account type',
        values: ['Individual account'],
        evidenceID: 'toAccountType',
      },
      {
        label: content?.accountRegistrationLabel || 'Account registration',
        values: [name],
        evidenceID: 'toAccountRegistration',
      },
      {
        label: content?.accountNumberLabel || 'Account number',
        values: [
          toAccount?.accountNumber === 'newAccount' ||
          !toAccount?.accountNumber ||
          openAccount.openAccountResponse.isS24Reject
            ? 'Account number pending'
            : toAccount.accountNumber,
        ],
        evidenceID: 'toAccountNumber',
      },
      {
        label: content?.dividendsAndCapitalGainsLabel || 'Dividends & capital gains',
        values: ['Reinvest distributions'],
        modal: ReceiptModal.DIVCAPGAINS,
        evidenceID: 'divAndCap',
      },
      {
        label: content?.settlementFundLabel || 'Settlement fund',
        values: [SettlementFundMap[settlementFund]],
        modal: ReceiptModal.SETTLEMENTFUND,
        evidenceID: 'settlementFund',
        additionalText: getSettlementFundAdditionalText(flowType === FlowTypes.MINOR, content),
      },
    );
  }
  return toAccountRows;
}

function getSettlementFundAdditionalText(
  isMinor: boolean,
  content?: typeof ReviewContent.receiptContent.transferFromAndTo,
) {
  const settlementFundDescription =
    content?.settlementFundDescription || ReviewContent.settlementFundDescription;
  const fundInvestWarning = content?.fundInvestWarning || ReviewContent.fundInvestWarning;
  return isMinor ? settlementFundDescription : fundInvestWarning;
}

export function buildToEstateAccountRow(
  triageState: TriageState,
  estateState: EstateState,
  decedentName: string,
  editable: boolean,
) {
  const toAccountRows: ReceiptRow[] = [];
  const tin: string = editable
    ? estateState.tin_private!
    : '**-***' + splitClientId(estateState.tin_private!);

  toAccountRows.push(
    {
      label: 'Account type',
      values: ['Individual estate account'],
      evidenceID: 'toAccountType',
    },
    {
      label: 'Estate name',
      values: [`Estate of ${decedentName}`],
      evidenceID: 'toAccountRegistration',
    },
    {
      label: 'Estate tax identification number',
      values: [tin],
      evidenceID: 'toAccountNumber',
    },
  );

  if (triageState.flowType === FlowTypes.INHERITANCE) {
    toAccountRows.push(
      {
        label: 'Dividends & capital gains',
        values: ['Reinvest distributions'],
        modal: ReceiptModal.DIVCAPGAINS,
        evidenceID: 'divAndCap',
      },
      {
        label: 'Settlement fund',
        values: ['Vanguard Federal Money Market Fund'],
        modal: ReceiptModal.SETTLEMENTFUND,
        evidenceID: 'settlementFund',
        additionalText: ReviewContent.fundInvestWarning,
      },
    );
  }

  return toAccountRows;
}

export function buildAccountToNonretirement(
  personalInfo: MfePersonalInfoClientState['personalInfo'],
  selectedAccounts: ISelectedClientAccountsState,
  triageState: TriageState,
  estateState: EstateState,
  decedentName: string,
  openAccountState: IOpenAccountState,
  editable: boolean,
  settlementFund?: SettlementFundAoAEnum | null,
  content?: typeof ReviewContent.receiptContent.transferFromAndTo,
): IReceipt {
  const flow = urlMapper[triageState.flowType];
  const individualEstate =
    triageState.flowType === FlowTypes.INHERITANCE &&
    triageState.individualDesignation === IndividualDesignationListedTypes.EXECUTOR;
  const editRoute = individualEstate ? `secure/${flow}/estate` : `secure/${flow}/transfer-to`;

  const toAccountReceipt: IReceipt = {
    header: 'Transferring to',
    description: null,
    editable: editable,
    editRoute: editRoute,
    sectionEvidenceID: 'accountTo',
    rows: [],
  };

  toAccountReceipt.rows = individualEstate
    ? buildToEstateAccountRow(triageState, estateState, decedentName, editable)
    : buildToAccountRow(
        personalInfo,
        selectedAccounts,
        openAccountState,
        settlementFund ?? SettlementFundAoAEnum.FED,
        triageState.flowType,
        content,
      );
  return toAccountReceipt;
}

export function buildAccountToMultiInheritance(
  currentTransaction: TransactionState | null,
  VCDToggleOn: boolean,
  multiEstateToggleOn: boolean,
  individualJointToTrustToggleOn: boolean,
  content?: typeof ReviewContent.receiptContent.transferFromAndTo,
  financialContent?: typeof ReviewContent.receiptContent.financialProfile,
  editable?: boolean,
): IReceipt {
  const toAccountReceipt: IReceipt = {
    header: 'Account',
    description: null,
    editable: editable ?? true,
    editRoute: 'secure/multi-inheritance/account-triage',
    sectionEvidenceID: 'accountTo',
    editTransferId: currentTransaction?.currentTransactionId,
    rows: [],
  };
  const objectives =
    currentTransaction?.investmentObjectives &&
    buildInvestmentObjectivesMultiDescription(currentTransaction?.investmentObjectives);

  const uid = uuidv4();

  toAccountReceipt.rows = [
    {
      label: content?.accountTypeLabel || 'Account type',
      values: [
        !!currentTransaction && !!currentTransaction.selectedClientAccounts.transferTo
          ? getMultiInheritanceAccountToType(
              currentTransaction,
              multiEstateToggleOn,
              individualJointToTrustToggleOn,
            )
          : 'Unknown',
      ],
      evidenceID: 'toAccountType',
    },
    {
      label: content?.accountNumberLabel || 'Account number',
      values: ['Account number pending'],
      evidenceID: 'toAccountNumber',
    },
    {
      label: content?.dividendsAndCapitalGainsLabel || 'Dividends & capital gains',
      values: ['Reinvest distributions'],
      modal: `${ReceiptModal.DIVCAPGAINS}_${uid}`,
      evidenceID: 'divAndCap',
    },
    {
      label: 'Source of Funds',
      values: ['Gift/Inheritance'],
      evidenceID: 'sourceOfFunds',
    },
    buildSettlementFundRow(
      VCDToggleOn,
      currentTransaction?.settlementFund,
      `${ReceiptModal.SETTLEMENTFUND}_${uid}`,
    ),
    {
      label: financialContent?.investmentObjectiveLabel || 'Investment objective',
      values: [objectives || ''],
      evidenceID: 'investmentObjectives',
    },
  ];
  return toAccountReceipt;
}

export function accountName(account: ISelectedAccount) {
  // A draft-hydrated selection is a partial cast to the full account type, so the compiler
  // cannot see accountName is absent. Unguarded, this throws during HydrateDraft and strands
  // the client on the page.
  const name = account.accountData?.accountName;

  if (!name) {
    return '';
  }

  const isBrokerage = !!account.accountData?.productType?.includes('BROKERAGE');
  const registration = isBrokerage ? name.substr(0, name.indexOf('—') - 1) : name;

  return registration.replace(',', ' &');
}

export function buildInvestmentObjectivesMultiDescription(invObj: InvestmentObjectives) {
  let objectives = '';
  const objectiveKeys = Object.keys(invObj);
  objectiveKeys.forEach((key) => {
    if (invObj[key]) {
      const label = key === 'CAP' ? 'Capital preservation' : key;
      objectives += label + '<br>';
    }
  });
  return objectives;
}
